import React, { useEffect, useRef, useState } from "react";
import { CalendarPlus, CheckCircle2 } from "lucide-react";
import { AppButton, AppModal, Callout, FieldGroup, FormField, NativeSelect, Radio } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { formatDay } from "./scheduleFormat";

const FORM_ID = "syllabus-generate-form";

/**
 * GeneratePlanDialog — a batch with no class plan gets one from the course's
 * syllabus: a batch that went Ongoing before its syllabus existed, or before a
 * module-wise syllabus formed classes.
 *
 * The admin says where the plan starts (the batch's start, or today) and how
 * far the batch already got ("taught up to class N" — those are recorded as
 * taught before the plan). The server's preview shows every class with its date.
 *
 * Props: show, onHide, batchId, batchStartedBefore (start date before today), onDone(message)
 */
const GeneratePlanDialog = ({ show, onHide, batchId, batchStartedBefore = false, onDone }) => {
  const [startFrom, setStartFrom] = useState("batchStart");
  const [taughtThrough, setTaughtThrough] = useState(0);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const request = useRef(0);

  useEffect(() => {
    if (!show) return;
    setStartFrom(batchStartedBefore ? "today" : "batchStart");
    setTaughtThrough(0);
    setPreview(null);
    setFailure("");
    setSaving(false);
  }, [show, batchStartedBefore]);

  useEffect(() => {
    if (!show) return undefined;
    const id = (request.current += 1);
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.post(`/syllabus/batch/${batchId}/sync-syllabus/preview`, { startFrom, taughtThrough });
        if (request.current === id) { setPreview(data); setFailure(""); }
      } catch (err) {
        if (request.current === id) {
          setPreview(null);
          setFailure(err?.response?.data?.message || "The plan could not be worked out.");
        }
      } finally {
        if (request.current === id) setChecking(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [show, batchId, startFrom, taughtThrough]);

  const unit = preview?.format === "module" ? "class" : "day";

  const submit = async (e) => {
    e.preventDefault();
    if (!preview) return;
    setSaving(true);
    setFailure("");
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/sync-syllabus`, { startFrom, taughtThrough });
      onDone?.(data.message);
      onHide();
    } catch (err) {
      setFailure(err?.response?.data?.message || "The plan was not generated. Try again.");
      setSaving(false);
    }
  };

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      size="lg"
      title="Plan this batch’s classes"
      icon={<CalendarPlus size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onHide} disabled={saving}>Cancel</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={saving || checking || !preview}>
            {saving ? "Planning…" : `Plan ${preview?.totalClasses || ""} ${unit}${preview?.totalClasses === 1 ? "" : unit === "class" ? "es" : "s"}`}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-5">
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          The plan is built from the course’s syllabus{preview?.classMinutes ? `, in classes of ${preview.classMinutes} minutes` : ""}, on this
          batch’s class days with holidays skipped.
        </p>
        {failure && <Callout tone="danger" role="alert">{failure}</Callout>}

        <FieldGroup legend="Date the plan from">
          <div className="tw-grid tw-gap-2 min-[576px]:tw-grid-cols-2">
            <Radio
              variant="card"
              name="gp-start"
              value="today"
              checked={startFrom === "today"}
              onChange={() => setStartFrom("today")}
              labelClassName="tw-font-semibold"
              label="Today"
              description="The next class to teach goes on the next class day."
            />
            <Radio
              variant="card"
              name="gp-start"
              value="batchStart"
              checked={startFrom === "batchStart"}
              onChange={() => setStartFrom("batchStart")}
              labelClassName="tw-font-semibold"
              label="The batch’s start"
              description="Classes not taught by now show as overdue."
            />
          </div>
        </FieldGroup>

        <FormField label="Already taught" hint="Those are recorded as taught before the plan.">
          <NativeSelect value={taughtThrough} onChange={(e) => setTaughtThrough(Number(e.target.value))}>
            <option value={0}>Nothing yet</option>
            {(preview?.classes || []).map((c) => (
              <option key={c.dayNumber} value={c.dayNumber}>Up to {c.label} — {c.title}</option>
            ))}
          </NativeSelect>
        </FormField>

        <section aria-live="polite" className="tw-flex tw-flex-col tw-gap-2">
          {checking && !preview && <Shimmer height={44} count={4} />}
          {preview && (
            <>
              <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
                {preview.totalClasses} {unit}{preview.totalClasses === 1 ? "" : unit === "class" ? "es" : "s"}
                {preview.firstDate && <span className="tw-font-normal tw-text-content-muted"> · next on {formatDay(preview.firstDate)}</span>}
                {preview.lastDate && <span className="tw-font-normal tw-text-content-muted"> · last on {formatDay(preview.lastDate, { year: true })}</span>}
              </p>
              <ol className="tw-m-0 tw-flex tw-max-h-72 tw-list-none tw-flex-col tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-0">
                {preview.classes.map((c) => (
                  <li key={c.dayNumber} className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-1.5 last:tw-border-b-0">
                    <span className="tw-w-16 tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-muted">{c.label}</span>
                    <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.8125rem] tw-text-content" title={c.title}>{c.title}</span>
                    {c.taught ? (
                      <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-success-fg">
                        <CheckCircle2 size={12} aria-hidden="true" /> Taught
                      </span>
                    ) : (
                      <span className="tw-shrink-0 tw-text-xs tw-text-content-muted">{formatDay(c.plannedDate)}</span>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )}
        </section>
      </form>
    </AppModal>
  );
};

export default GeneratePlanDialog;
